import { productEngine } from '@/engines/product-intelligence'
import type { ProductModel } from '@/engines/product-intelligence'
import { comparisonEngine } from './ComparisonEngine'
import { comparisonHistory } from './ComparisonHistory'
import type {
  ComparisonBuildResult,
  ComparisonFiltersState,
  ComparisonOfferInput,
  ComparisonPreferences,
  SupplierChannel,
} from './ComparisonTypes'
import { DEFAULT_COMPARISON_FILTERS, DEFAULT_COMPARISON_PREFERENCES } from './ComparisonTypes'

export interface RawSupplierOffer {
  id: string
  description: string
  supplierId: string
  supplierName: string
  channel: SupplierChannel
  price: number
  currency?: 'BRL' | 'USD'
  stock: number
  deliveryDays: number
  updatedAt?: string
  sku?: string
  purchasePrice?: number
  marginPercent?: number
}

/**
 * ComparisonManager — orchestrates Product Intelligence → unified comparison.
 */
export class ComparisonManager {
  private offers: ComparisonOfferInput[] = []
  private filters: ComparisonFiltersState = { ...DEFAULT_COMPARISON_FILTERS }
  private preferences: ComparisonPreferences = { ...DEFAULT_COMPARISON_PREFERENCES }
  private selected = new Set<string>()
  private lastResult: ComparisonBuildResult | null = null

  reset(): void {
    this.offers = []
    this.selected.clear()
    this.lastResult = null
    productEngine.reset()
  }

  setFilters(filters: Partial<ComparisonFiltersState>) {
    this.filters = { ...this.filters, ...filters }
    return this.rebuild()
  }

  getFilters() {
    return { ...this.filters }
  }

  setPreferences(preferences: Partial<ComparisonPreferences>) {
    this.preferences = { ...this.preferences, ...preferences }
    return this.rebuild()
  }

  getPreferences() {
    return { ...this.preferences }
  }

  /**
   * Ingest raw supplier ads → Product Intelligence recognize → fingerprint offers.
   */
  ingest(rawOffers: RawSupplierOffer[], options: { recordHistory?: boolean } = {}) {
    const mapped: ComparisonOfferInput[] = rawOffers.map((raw) => {
      const product: ProductModel = productEngine.recognize(raw.description, { sku: raw.sku })
      return {
        offerId: raw.id,
        fingerprint: product.fingerprint,
        product,
        supplierId: raw.supplierId,
        supplierName: raw.supplierName,
        channel: raw.channel,
        price: raw.price,
        currency: raw.currency ?? 'BRL',
        stock: raw.stock,
        deliveryDays: raw.deliveryDays,
        updatedAt: raw.updatedAt ?? new Date().toISOString(),
        purchasePrice: raw.purchasePrice,
        marginPercent: raw.marginPercent,
      }
    })

    this.offers = mapped
    if (options.recordHistory !== false) {
      comparisonEngine.snapshotPrices(mapped)
    }
    return this.rebuild()
  }

  toggleSelect(fingerprint: string) {
    if (this.selected.has(fingerprint)) this.selected.delete(fingerprint)
    else this.selected.add(fingerprint)
    return this.rebuild()
  }

  selectMany(fingerprints: string[]) {
    for (const fp of fingerprints) this.selected.add(fp)
    return this.rebuild()
  }

  clearSelection() {
    this.selected.clear()
    return this.rebuild()
  }

  getSelectedFingerprints() {
    return [...this.selected]
  }

  getSelectedOffers(): ComparisonOfferInput[] {
    const result = this.lastResult
    if (!result) return []
    return result.rows
      .filter((row) => this.selected.has(row.fingerprint))
      .map((row) => {
        const best = row.offers.find((o) => o.isBestPrice) ?? row.offers[0]!
        return best
      })
  }

  getResult() {
    return this.lastResult ?? this.rebuild()
  }

  listPriceHistory(fingerprint?: string) {
    return comparisonHistory.list(fingerprint)
  }

  private rebuild() {
    this.lastResult = comparisonEngine.build(
      this.offers,
      this.filters,
      this.preferences,
      [...this.selected],
    )
    return this.lastResult
  }
}

export const comparisonManager = new ComparisonManager()
